import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Hook that instantly positions to top of page on route change
 * This ensures users start at the top when navigating between pages
 * Maintains scroll position on page refresh
 */
export const useScrollToTop = () => {
  const { pathname } = useLocation()
  const prevPathname = useRef<string | null>(null)
  const isInitialMount = useRef(true)

  useEffect(() => {
    const scrollKey = `scroll-${pathname}`

    if (isInitialMount.current) {
      // On initial mount (page refresh), restore saved scroll position
      isInitialMount.current = false
      const savedScroll = sessionStorage.getItem(scrollKey)
      if (savedScroll) {
        const scrollPos = parseInt(savedScroll, 10)
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => {
          window.scrollTo({ top: scrollPos, left: 0, behavior: 'instant' })
        }, 0)
      }
    } else if (prevPathname.current !== pathname && prevPathname.current !== null) {
      // Navigation happened - delay scroll to top to allow exit animation to complete
      // This prevents the glitchy look of scrolling during fade out
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
      }, 200) // Match the exit animation duration
    }

    prevPathname.current = pathname

    // Save scroll position before unmount or navigation
    const saveScrollPosition = () => {
      sessionStorage.setItem(scrollKey, window.scrollY.toString())
    }

    window.addEventListener('beforeunload', saveScrollPosition)

    return () => {
      saveScrollPosition()
      window.removeEventListener('beforeunload', saveScrollPosition)
    }
  }, [pathname])
}
